import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FaPlus, FaTrash, FaExternalLinkAlt, FaSave } from 'react-icons/fa';
import axiosInstance from '../../../shared/utils.js/axiosConfig';
import { BASE_URL } from '../../../shared/utils.js/utils';
import { useTitle } from '../../../shared/hooks/TitleContext';

const COLORS = {
  primary: '#11324D',
  accent: '#AC2000',
  neutral: '#B3B3B3',
};

const ICON_OPTIONS = [
  { value: 'design', label: 'تصميم' },
  { value: 'supervision', label: 'إشراف ومتابعة' },
  { value: 'soil', label: 'اختبارات تربة' },
  { value: 'estimation', label: 'تقديرات ومقايسات' },
  { value: 'default', label: 'عام' },
];

const EMPTY_DATA = {
  hero: { title_ar: '', title_en: '', subtitle_ar: '', subtitle_en: '' },
  about: { text_ar: '', text_en: '' },
  services: [],
  features: [],
  stats: [],
  contact: {
    phone: '',
    whatsapp: '',
    email: '',
    address_ar: '',
    address_en: '',
    facebook: '',
    linkedin: '',
    instagram: '',
  },
};

const WebsiteSettings = () => {
  const { setTitle } = useTitle();
  const [data, setData] = useState(EMPTY_DATA);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setTitle('إعدادات الموقع الإلكتروني');
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const res = await axiosInstance.get(`${BASE_URL}/website-settings`);
      const payload = res.data?.data || {};
      setData({
        hero: { ...EMPTY_DATA.hero, ...(payload.hero || {}) },
        about: { ...EMPTY_DATA.about, ...(payload.about || {}) },
        services: Array.isArray(payload.services) ? payload.services : [],
        features: Array.isArray(payload.features) ? payload.features : [],
        stats: Array.isArray(payload.stats) ? payload.stats : [],
        contact: { ...EMPTY_DATA.contact, ...(payload.contact || {}) },
      });
    } catch (error) {
      toast.error('تعذر تحميل بيانات الموقع');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axiosInstance.put(`${BASE_URL}/website-settings`, { data });
      toast.success('تم حفظ بيانات الموقع بنجاح');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'تعذر حفظ بيانات الموقع');
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (section, field, value) => {
    setData((prev) => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
  };

  const updateListItem = (listKey, index, field, value) => {
    setData((prev) => {
      const list = [...prev[listKey]];
      list[index] = { ...list[index], [field]: value };
      return { ...prev, [listKey]: list };
    });
  };

  const addListItem = (listKey, emptyItem) => {
    setData((prev) => ({ ...prev, [listKey]: [...prev[listKey], emptyItem] }));
  };

  const removeListItem = (listKey, index) => {
    setData((prev) => ({ ...prev, [listKey]: prev[listKey].filter((_, i) => i !== index) }));
  };

  const cardStyle = {
    background: '#1b3350',
    borderRadius: 12,
    padding: 24,
    marginBottom: 20,
    border: '1px solid #2c4a70',
  };
  const labelStyle = { display: 'block', marginBottom: 6, fontSize: 14, color: '#ccc' };
  const inputStyle = {
    width: '100%',
    padding: 10,
    backgroundColor: '#14263b',
    border: '1px solid #333',
    borderRadius: 8,
    color: 'white',
    fontSize: 14,
    marginBottom: 14,
  };
  const textareaStyle = { ...inputStyle, minHeight: 90, resize: 'vertical' };
  const sectionTitleStyle = { color: 'white', fontSize: 16, fontWeight: 700, marginBottom: 16 };
  const itemBoxStyle = {
    background: '#1a1f2e',
    border: '1px solid #2a3446',
    borderRadius: 10,
    padding: 16,
    marginBottom: 14,
    position: 'relative',
  };
  const removeBtnStyle = {
    position: 'absolute',
    top: 12,
    left: 12,
    background: '#c0392b',
    border: 'none',
    color: 'white',
    borderRadius: 6,
    padding: '6px 8px',
    cursor: 'pointer',
  };
  const addBtnStyle = {
    background: 'transparent',
    border: `1px dashed ${COLORS.neutral}`,
    color: '#ccc',
    borderRadius: 8,
    padding: '10px 16px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    justifyContent: 'center',
  };

  if (isLoading) {
    return (
      <div style={{ padding: 30, color: 'white', textAlign: 'center' }}>جاري التحميل...</div>
    );
  }

  return (
    <div style={{ padding: '30px', backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ margin: 0, fontSize: 20 }}>إعدادات الموقع الإلكتروني</h1>
        <div style={{ display: 'flex', gap: 12 }}>
          <a
            href="https://home.albarqawy.brmja.cloud"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              backgroundColor: '#444',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '10px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <FaExternalLinkAlt /> معاينة الموقع
          </a>
          <button
            onClick={handleSave}
            disabled={isSaving}
            style={{
              backgroundColor: isSaving ? '#666' : COLORS.accent,
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '10px 20px',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontWeight: 700,
            }}
          >
            <FaSave /> {isSaving ? 'جاري الحفظ...' : 'حفظ التغييرات'}
          </button>
        </div>
      </div>

      {/* Hero */}
      <div style={cardStyle}>
        <div style={sectionTitleStyle}>القسم الرئيسي (Hero)</div>
        <label style={labelStyle}>العنوان الرئيسي</label>
        <input
          style={inputStyle}
          value={data.hero.title_ar}
          onChange={(e) => updateField('hero', 'title_ar', e.target.value)}
        />
        <label style={labelStyle}>الوصف الفرعي</label>
        <textarea
          style={textareaStyle}
          value={data.hero.subtitle_ar}
          onChange={(e) => updateField('hero', 'subtitle_ar', e.target.value)}
        />
      </div>

      {/* About */}
      <div style={cardStyle}>
        <div style={sectionTitleStyle}>من نحن</div>
        <label style={labelStyle}>نص التعريف بالمكتب</label>
        <textarea
          style={{ ...textareaStyle, minHeight: 120 }}
          value={data.about.text_ar}
          onChange={(e) => updateField('about', 'text_ar', e.target.value)}
        />
      </div>

      {/* Services */}
      <div style={cardStyle}>
        <div style={sectionTitleStyle}>خدماتنا</div>
        {data.services.map((service, idx) => (
          <div key={idx} style={itemBoxStyle}>
            <button style={removeBtnStyle} onClick={() => removeListItem('services', idx)} title="حذف">
              <FaTrash />
            </button>
            <label style={labelStyle}>الأيقونة</label>
            <select
              style={inputStyle}
              value={service.icon || 'default'}
              onChange={(e) => updateListItem('services', idx, 'icon', e.target.value)}
            >
              {ICON_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <label style={labelStyle}>اسم الخدمة</label>
            <input
              style={inputStyle}
              value={service.title_ar || ''}
              onChange={(e) => updateListItem('services', idx, 'title_ar', e.target.value)}
            />
            <label style={labelStyle}>وصف الخدمة</label>
            <textarea
              style={textareaStyle}
              value={service.description_ar || ''}
              onChange={(e) => updateListItem('services', idx, 'description_ar', e.target.value)}
            />
          </div>
        ))}
        <button
          style={addBtnStyle}
          onClick={() =>
            addListItem('services', { icon: 'default', title_ar: '', title_en: '', description_ar: '', description_en: '' })
          }
        >
          <FaPlus /> إضافة خدمة
        </button>
      </div>

      {/* Features */}
      <div style={cardStyle}>
        <div style={sectionTitleStyle}>لماذا نحن</div>
        {data.features.map((feature, idx) => (
          <div key={idx} style={itemBoxStyle}>
            <button style={removeBtnStyle} onClick={() => removeListItem('features', idx)} title="حذف">
              <FaTrash />
            </button>
            <label style={labelStyle}>العنوان</label>
            <input
              style={inputStyle}
              value={feature.title_ar || ''}
              onChange={(e) => updateListItem('features', idx, 'title_ar', e.target.value)}
            />
            <label style={labelStyle}>الوصف</label>
            <textarea
              style={textareaStyle}
              value={feature.description_ar || ''}
              onChange={(e) => updateListItem('features', idx, 'description_ar', e.target.value)}
            />
          </div>
        ))}
        <button
          style={addBtnStyle}
          onClick={() => addListItem('features', { title_ar: '', title_en: '', description_ar: '', description_en: '' })}
        >
          <FaPlus /> إضافة ميزة
        </button>
      </div>

      {/* Contact */}
      <div style={cardStyle}>
        <div style={sectionTitleStyle}>بيانات التواصل</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0 20px' }}>
          <div>
            <label style={labelStyle}>رقم الهاتف</label>
            <input
              style={inputStyle}
              value={data.contact.phone}
              onChange={(e) => updateField('contact', 'phone', e.target.value)}
              placeholder="01xxxxxxxxx"
            />
          </div>
          <div>
            <label style={labelStyle}>رقم الواتساب</label>
            <input
              style={inputStyle}
              value={data.contact.whatsapp}
              onChange={(e) => updateField('contact', 'whatsapp', e.target.value)}
              placeholder="01xxxxxxxxx"
            />
          </div>
          <div>
            <label style={labelStyle}>البريد الإلكتروني</label>
            <input
              style={inputStyle}
              value={data.contact.email}
              onChange={(e) => updateField('contact', 'email', e.target.value)}
              placeholder="info@example.com"
            />
          </div>
          <div>
            <label style={labelStyle}>العنوان</label>
            <input
              style={inputStyle}
              value={data.contact.address_ar}
              onChange={(e) => updateField('contact', 'address_ar', e.target.value)}
            />
          </div>
          <div>
            <label style={labelStyle}>فيسبوك (رابط كامل)</label>
            <input
              style={inputStyle}
              value={data.contact.facebook}
              onChange={(e) => updateField('contact', 'facebook', e.target.value)}
              placeholder="https://facebook.com/..."
            />
          </div>
          <div>
            <label style={labelStyle}>لينكدإن (رابط كامل)</label>
            <input
              style={inputStyle}
              value={data.contact.linkedin}
              onChange={(e) => updateField('contact', 'linkedin', e.target.value)}
              placeholder="https://linkedin.com/..."
            />
          </div>
          <div>
            <label style={labelStyle}>إنستجرام (رابط كامل)</label>
            <input
              style={inputStyle}
              value={data.contact.instagram}
              onChange={(e) => updateField('contact', 'instagram', e.target.value)}
              placeholder="https://instagram.com/..."
            />
          </div>
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={isSaving}
        style={{
          backgroundColor: isSaving ? '#666' : COLORS.accent,
          color: '#fff',
          border: 'none',
          borderRadius: 8,
          padding: '12px 24px',
          cursor: isSaving ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontWeight: 700,
          marginTop: 4,
        }}
      >
        <FaSave /> {isSaving ? 'جاري الحفظ...' : 'حفظ التغييرات'}
      </button>
    </div>
  );
};

export default WebsiteSettings;
